/**
 * @module useFilter
 * Shared filter state and table row expansion for the sales data views
 * (SalesMonthly, SalesDaily, SalesMonthlyAverages, SalesChart).
 *
 * All refs live at module scope so filter state persists while navigating
 * between sales sub-pages within the same session. Clearing filters or
 * logging out resets them.
 *
 * Filter values are kept in a `reactive` object (not individual refs) so
 * components can `v-model` directly onto `filters.city` etc., and the
 * `activeFilters` computed can iterate all keys in a single loop.
 *
 * The hierarchy used in table expansion state is: City > Location > Brand.
 * Composite keys ("city-location", "city-location-brand") are used instead
 * of nested objects so Vue's reactivity can track changes to arbitrary keys.
 */

import { ref, reactive, computed, watch } from "vue";

// ── Global filter state ──────────────────────────────────────────────────────

const isFilterExpanded = ref(false);
const loading = ref(false);
const exporting = ref(false);
const filterApplied = ref(false);
const filterCleared = ref(false);

const filters = reactive({
  city: null,
  location: null,
  brand: null,
  platform: null,
  source: null,
  start_date: "",
  end_date: "",
  created_at_start: "",
  created_at_end: "",
  exclude_zero: "",
});

const dateRangePreset = ref("");

const datePresets = [
  { label: "Today", value: "0d" },
  { label: "Yesterday", value: "1d" },
  { label: "Last 7 days", value: "7d" },
  { label: "Last 30 days", value: "30d" },
  { label: "This month", value: "month" },
  { label: "Last month", value: "last_month" },
];

// ── Totals & summary ─────────────────────────────────────────────────────────

const filterTotals = ref(null);

/**
 * Derived financial summary from the API totals.
 * Separates discount% and fees% so the toolbar can show them independently.
 * discountPct and feesPct are 0 when grossAmount is missing.
 */
const totalsSummary = computed(() => {
  if (!filterTotals.value) return null;
  const grossAmount = filterTotals.value.grossAmount || 0;
  const amount = filterTotals.value.amount || 0;
  const discount = filterTotals.value.discount || 0;
  const commissions = filterTotals.value.commissions || 0;
  const onlinePayments = filterTotals.value.onlinePayments || 0;
  // Total fees = delivery platform commissions + payment processing fees
  const fees = commissions + onlinePayments;
  const discountPct = grossAmount > 0 ? (discount / grossAmount) * 100 : 0;
  const feesPct = grossAmount > 0 ? (fees / grossAmount) * 100 : 0;
  return {
    amount,
    amount_avg: filterTotals.value.amount_avg,
    number_of_days: filterTotals.value.number_of_days,
    orders: filterTotals.value.orders,
    records: filterTotals.value.records,
    excludedOrders: filterTotals.value.excludedOrders || 0,
    grossAmount,
    discount,
    discountPct,
    commissions,
    onlinePayments,
    fees,
    feesPct,
  };
});

// ── Table expansion state ────────────────────────────────────────────────────

// Reactive objects instead of Map/Set so Vue tracks property additions/deletions
const expandedCities = reactive({});
const expandedLocations = reactive({});
const expandedBrands = reactive({});

// ── Dark theme (used by chart components that need raw JS colors) ─────────────

const darkTheme = {
  textColor: "#ffffff",
  axisLineColor: "#666666",
  splitLineColor: "#333333",
  tooltipBackground: "#2a2a2a",
  tooltipBorder: "#444444",
  seriesColor: "#4fc3f7",
  areaGradient: {
    start: "rgba(79, 195, 247, 0.3)",
    end: "rgba(79, 195, 247, 0.1)",
  },
};

// ── Utility functions (exported for reuse) ────────────────────────────────────

export function shallowEqual(obj1, obj2) {
  const keys1 = Object.keys(obj1);
  const keys2 = Object.keys(obj2);

  if (keys1.length !== keys2.length) return false;

  return keys1.every((key) => obj1[key] === obj2[key]);
}

export function deepEqual(obj1, obj2) {
  if (obj1 === obj2) return true;

  if (typeof obj1 !== "object" || obj1 === null || typeof obj2 !== "object" || obj2 === null) {
    return false;
  }

  const keys1 = Object.keys(obj1);
  const keys2 = Object.keys(obj2);

  if (keys1.length !== keys2.length) return false;

  return keys1.every((key) => keys2.includes(key) && deepEqual(obj1[key], obj2[key]));
}

const safeArray = (arr) => {
  return Array.isArray(arr) ? arr : [];
};

/** Safely traverse a nested object by an array of keys without throwing. */
const getSafeValue = (obj, path, defaultValue = 0) => {
  if (!obj) return defaultValue;
  return path.reduce((current, key) => {
    return current && current[key] !== undefined ? current[key] : defaultValue;
  }, obj);
};

const formatMonthForDisplay = (monthString) => {
  const [year, month] = monthString.split("-");
  const date = new Date(year, month - 1);
  return date.toLocaleDateString("en-US", { year: "numeric", month: "short" });
};

// Compact axis label: "7 Jan\nM" (day, abbreviated month, weekday initial)
const formatDayForAxis = (dateString) => {
  const date = new Date(dateString);
  const day = date.getDate();
  const month = date.toLocaleDateString("en-US", { month: "short" });
  const weekday = date.toLocaleDateString("en-US", { weekday: "short" }).charAt(0);
  return `${day} ${month}\n${weekday}`;
};

// Verbose tooltip label: "15 Jan (Mon)"
const formatDayForDisplay = (dateString) => {
  const date = new Date(dateString);
  const day = date.getDate();
  const month = date.toLocaleDateString("en-US", { month: "short" });
  const weekday = date.toLocaleDateString("en-US", { weekday: "short" });
  return `${day} ${month} (${weekday})`;
};

const formatAmount = (amount, minDigit = 2, maxDigit = 2) => {
  return new Intl.NumberFormat("en-US", {
    minimumFractionDigits: minDigit,
    maximumFractionDigits: maxDigit,
  }).format(amount);
};

// ── Composable ────────────────────────────────────────────────────────────────

export function useFilter() {
  // ── Active filter computeds ──────────────────────────────────────────────────

  /** Strips empty/null values so the API doesn't receive blank query params. */
  const activeFilters = computed(() => {
    return Object.entries(filters).reduce((acc, [key, value]) => {
      if (value && String(value).trim() !== "") acc[key] = value;
      return acc;
    }, {});
  });

  const numberOfActiveFilters = computed(() => {
    return Object.keys(activeFilters.value).length;
  });

  const hasActiveFilters = computed(() => {
    return numberOfActiveFilters.value > 0 || dateRangePreset.value !== "";
  });

  const filterSummary = computed(() => {
    const active = activeFilters.value;
    if (Object.keys(active).length === 0) return "No filters applied";

    return Object.entries(active)
      .map(([key, value]) => `${key}: ${value}`)
      .join(", ");
  });

  // Human-readable labels for filter chip display
  const filterLabels = {
    city: "City",
    location: "Location",
    brand: "Brand",
    platform: "Platform",
    source: "Source",
    start_date: "From",
    end_date: "To",
    created_at_start: "Entered From",
    created_at_end: "Entered To",
    exclude_zero: "Non-zero",
  };

  const filterChips = computed(() => {
    const chips = [];
    for (const [key, value] of Object.entries(activeFilters.value)) {
      let displayValue = value;
      if (key.includes("date") || key.includes("created_at")) {
        displayValue = new Date(value).toLocaleDateString();
      }
      if (key === "exclude_zero") {
        displayValue = "Yes";
      }
      chips.push({
        key,
        label: filterLabels[key] || key,
        value: displayValue,
      });
    }
    return chips;
  });

  /** Remove a single filter chip without clearing everything else. */
  const removeFilter = (key) => {
    if (key in filters) {
      // Select filters reset to null; text/date filters reset to empty string
      const selectFilters = ["city", "location", "brand", "platform", "source"];
      filters[key] = selectFilters.includes(key) ? null : "";
    }
    if (key === "start_date" || key === "end_date") {
      dateRangePreset.value = "";
    }
  };

  // ── Table expansion helpers ──────────────────────────────────────────────────

  const isCityExpanded = (city) => {
    return expandedCities[city] === true;
  };

  const isLocationExpanded = (city, location) => {
    return expandedLocations[`${city}-${location}`] === true;
  };

  const isBrandExpanded = (city, location, brand) => {
    return expandedBrands[`${city}-${location}-${brand}`] === true;
  };

  const toggleCity = (city) => {
    expandedCities[city] = !expandedCities[city];
  };

  const toggleLocation = (city, location) => {
    const key = `${city}-${location}`;
    expandedLocations[key] = !expandedLocations[key];
  };

  const toggleBrand = (city, location, brand) => {
    const key = `${city}-${location}-${brand}`;
    expandedBrands[key] = !expandedBrands[key];
  };

  const expandAll = () => {
    if (!chartData.value) return;

    getCitiesWithData().forEach((city) => {
      expandedCities[city] = true;

      getLocationsWithData(city).forEach((location) => {
        const locationKey = `${city}-${location}`;
        expandedLocations[locationKey] = true;

        getBrandsWithData(city, location).forEach((brand) => {
          const brandKey = `${city}-${location}-${brand}`;
          expandedBrands[brandKey] = true;
        });
      });
    });
  };

  const collapseAll = () => {
    // Delete keys rather than setting to false, to keep the objects lean
    Object.keys(expandedCities).forEach((key) => delete expandedCities[key]);
    Object.keys(expandedLocations).forEach((key) => delete expandedLocations[key]);
    Object.keys(expandedBrands).forEach((key) => delete expandedBrands[key]);
  };

  // ── Table data access functions ──────────────────────────────────────────────
  // These sum across all time units for a given city/location/brand/platform
  // combination. Used by the hierarchical matrix table rows.

  const getLocationCityTotal = (city, location) => {
    if (!chartData.value?.data) return 0;
    let total = 0;
    safeArray(chartData.value.brands).forEach((brand) => {
      safeArray(chartData.value.platforms).forEach((platform) => {
        safeArray(timeUnits.value).forEach((unit) => {
          total += getSafeValue(chartData.value.data, [city, location, brand, platform, unit], 0);
        });
      });
    });
    return total;
  };

  const getBrandLocationCityTotal = (city, location, brand) => {
    if (!chartData.value?.data) return 0;
    let total = 0;
    safeArray(chartData.value.platforms).forEach((platform) => {
      safeArray(timeUnits.value).forEach((unit) => {
        total += getSafeValue(chartData.value.data, [city, location, brand, platform, unit], 0);
      });
    });
    return total;
  };

  const getPlatformTotalForBrand = (city, location, brand, platform) => {
    if (!chartData.value?.data) return 0;
    let total = 0;
    safeArray(timeUnits.value).forEach((unit) => {
      total += getSafeValue(chartData.value.data, [city, location, brand, platform, unit], 0);
    });
    return total;
  };

  /** Total for a city/location pair on a specific day (used in daily table). */
  const getLocationCityDayTotal = (city, location, unit) => {
    if (!chartData.value?.data) return 0;
    let total = 0;
    safeArray(chartData.value.brands).forEach((brand) => {
      safeArray(chartData.value.platforms).forEach((platform) => {
        total += getSafeValue(chartData.value.data, [city, location, brand, platform, unit], 0);
      });
    });
    return total;
  };

  // ── Date preset handling ──────────────────────────────────────────────────────

  /**
   * Translate a preset key into concrete start/end dates and apply them to the
   * filters object. The preset label is also stored so the UI can show which
   * preset is active even after the dates are set.
   */
  const applyDatePreset = async (preset) => {
    if (!preset) {
      filters.start_date = "";
      filters.end_date = "";
      dateRangePreset.value = "";
      return;
    }

    dateRangePreset.value = preset;
    const today = new Date();
    const startDate = new Date();

    switch (preset) {
      case "0d": // Today
        startDate.setDate(today.getDate());
        const endDateToday = new Date();
        filters.start_date = formatDateForInput(startDate);
        filters.end_date = formatDateForInput(endDateToday);
        break;
      case "1d": // Yesterday
        startDate.setDate(today.getDate() - 1);
        const endDateYesterday = new Date();
        endDateYesterday.setDate(today.getDate() - 1);
        filters.start_date = formatDateForInput(startDate);
        filters.end_date = formatDateForInput(endDateYesterday);
        break;
      case "7d": // Last 7 days
        startDate.setDate(today.getDate() - 7);
        filters.start_date = formatDateForInput(startDate);
        filters.end_date = formatDateForInput(today);
        break;
      case "30d": // Last 30 days
        startDate.setDate(today.getDate() - 30);
        filters.start_date = formatDateForInput(startDate);
        filters.end_date = formatDateForInput(today);
        break;
      case "month": // This month (1st to today)
        startDate.setDate(1);
        filters.start_date = formatDateForInput(startDate);
        filters.end_date = formatDateForInput(today);
        break;
      case "last_month": // First to last day of previous month
        startDate.setMonth(today.getMonth() - 1, 1);
        const endDateLastMonth = new Date(today.getFullYear(), today.getMonth(), 0);
        filters.start_date = formatDateForInput(startDate);
        filters.end_date = formatDateForInput(endDateLastMonth);
        break;
    }
  };

  const formatDateForInput = (date) => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const day = String(date.getDate()).padStart(2, "0");
    return `${year}-${month}-${day}`;
  };

  // ── Filter actions ────────────────────────────────────────────────────────────

  const applyFilters = () => {
    filterApplied.value = true;
    filterCleared.value = false;
    // Collapse the filter panel so the data table has more screen space
    isFilterExpanded.value = false;
  };

  const clearFilters = () => {
    dateRangePreset.value = "";
    filterCleared.value = true;
    filterApplied.value = false;

    // Select-type filters use null (not "") so v-select shows the placeholder
    const selectFilters = ["city", "location", "brand", "platform", "source"];
    Object.keys(filters).forEach((key) => {
      filters[key] = selectFilters.includes(key) ? null : "";
    });
  };

  const setFilter = (key, value) => {
    if (key in filters) {
      filters[key] = value;
    }
  };

  const setFilters = (newFilters) => {
    Object.keys(newFilters).forEach((key) => {
      if (key in filters) {
        filters[key] = newFilters[key];
      }
    });
  };

  const toggleFilterExpand = () => {
    isFilterExpanded.value = !isFilterExpanded.value;
  };

  const resetState = () => {
    chartData.value = null;
    chartLoading.value = false;
    chartError.value = "";
    clearFilters();
  };

  // Formats AED amounts without decimals or currency symbol for compact display
  const formatCurrency = (amount) => {
    return new Intl.NumberFormat("en-US", {}).format(amount);
  };

  return {
    formatCurrency,
    filterTotals,
    totalsSummary,

    // Utility functions
    shallowEqual,
    deepEqual,
    safeArray,
    getSafeValue,
    formatMonthForDisplay,
    formatDayForDisplay,
    formatDayForAxis,
    formatAmount,

    // Filter state
    filterApplied: computed(() => filterApplied.value),
    filterCleared: computed(() => filterCleared.value),
    isFilterExpanded,
    toggleFilterExpand,
    filters,
    loading: computed(() => loading.value),
    exporting,
    datePresets,
    dateRangePreset,
    applyDatePreset,

    // Expanded states for table
    expandedCities,
    expandedLocations,
    expandedBrands,
    isCityExpanded,
    isLocationExpanded,
    isBrandExpanded,
    toggleCity,
    toggleLocation,
    toggleBrand,
    expandAll,
    collapseAll,

    // Computed properties
    activeFilters,
    hasActiveFilters,
    filterSummary,
    numberOfActiveFilters,
    filterChips,
    removeFilter,

    // Data access functions for table
    getLocationCityTotal,
    getBrandLocationCityTotal,
    getPlatformTotalForBrand,
    getLocationCityDayTotal,

    // Actions
    applyFilters,
    clearFilters,
    setFilter,
    setFilters,
    resetState,
  };
}
